import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import Analytics from './components/Analytics';
import RequireProfile from './components/RequireProfile';
import RequireEmployerProfile from './components/RequireEmployerProfile';
import {
  AuthenticatedEntryRedirect,
  SignedOutEmployerRouteRedirect,
  SignedOutWorkerRouteRedirect,
} from './components/AuthEntryRedirect';
import { checkAuth } from './store/authSlice';
import { RootState, AppDispatch } from './store';
import { getDefaultAuthenticatedPath } from './utils/authRedirect';
import './App.css';

// Public pages — eagerly loaded for SEO + fast first paint
import HomePage from './pages/HomePage';
import JobsPage from './pages/JobsPage';
import JobDetailsPage from './pages/JobDetailsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import RatingsPage from './pages/RatingsPage';
import HelpPage from './pages/HelpPage';
import EmployerLandingPage from './pages/EmployerLandingPage';
import EmployerFAQPage from './pages/EmployerFAQPage';
import WorkerLandingPage from './pages/WorkerLandingPage';
import WorkerFAQPage from './pages/WorkerFAQPage';
import CityGigsPage from './pages/CityGigsPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AboutPage from './pages/AboutPage';
import PricingPage from './pages/PricingPage';
import NotFoundPage from './pages/NotFoundPage';
import PublicWorkerProfilePage from './pages/PublicWorkerProfilePage';
import VendorsPage from './pages/VendorsPage';
import VendorDetailPage from './pages/VendorDetailPage';
import CityVendorsPage from './pages/CityVendorsPage';
import CityWorkerLandingPage from './pages/CityWorkerLandingPage';
import RolePage from './pages/RolePage';
import MarketPage from './pages/MarketPage';
import VendorSpecialtyCityPage from './pages/VendorSpecialtyCityPage';
import HireRoleCityPage from './pages/HireRoleCityPage';
import RoleCityGigsPage from './pages/RoleCityGigsPage';
import ClaimListingPage from './pages/ClaimListingPage';

// Authenticated pages — lazy loaded (only fetched after login)
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const WorkerProfileSetupPage = React.lazy(() => import('./pages/WorkerProfileSetupPage'));
const MatchesPage = React.lazy(() => import('./pages/MatchesPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const MessagesPage = React.lazy(() => import('./pages/MessagesPage'));
const WorkerInsightsPage = React.lazy(() => import('./pages/WorkerInsightsPage'));
const ApplicationDetailPage = React.lazy(() => import('./pages/ApplicationDetailPage'));
const ReferralPage = React.lazy(() => import('./pages/ReferralPage'));
const AvailabilityCalendarPage = React.lazy(() => import('./pages/AvailabilityCalendarPage'));
const PaymentsPage = React.lazy(() => import('./pages/PaymentsPage'));
const EmployerSetupPage = React.lazy(() => import('./pages/EmployerSetupPage'));
const EmployerDashboardPage = React.lazy(() => import('./pages/EmployerDashboardPage'));
const EmployerSubscriptionPage = React.lazy(() => import('./pages/EmployerSubscriptionPage'));
const PostGigPage = React.lazy(() => import('./pages/PostGigPage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const ApplicationsPage = React.lazy(() => import('./pages/ApplicationsPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const SavedJobsPage = React.lazy(() => import('./pages/SavedJobsPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));

/** Scrolls to top and moves focus to main content on route changes (WCAG 2.1). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.getElementById('main-content');
    if (main) main.focus({ preventScroll: true });
  }, [pathname]);
  return null;
}

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated, accessToken } = useSelector((state: RootState) => state.auth);
  const isRestoringAuthenticatedSession = Boolean(accessToken) && !isAuthenticated;

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  // Set Sentry user context for error attribution
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: String(user.id), email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  return (
    <Router>
      <ScrollToTop />
      <Analytics authenticated={isAuthenticated} />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        Skip to main content
      </a>
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main id="main-content" className="flex-grow" tabIndex={-1}>
          <ErrorBoundary>
          <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/jobs/:id" element={<JobDetailsPage />} />
            <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <AuthenticatedEntryRedirect role={user?.role} />} />
            <Route path="/register" element={!isAuthenticated ? <RegisterPage /> : <AuthenticatedEntryRedirect role={user?.role} />} />
            <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />} />
            <Route path="/reset-password" element={!isAuthenticated ? <ResetPasswordPage /> : <Navigate to="/dashboard" />} />
            <Route path="/profile/:id" element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" replace />} />
            <Route path="/ratings/:id" element={isAuthenticated ? <RatingsPage /> : <Navigate to="/login" replace />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/for-employers" element={<EmployerLandingPage />} />
            <Route path="/for-employers/faq" element={<EmployerFAQPage />} />
            <Route path="/for-workers" element={<WorkerLandingPage />} />
            <Route path="/for-workers/faq" element={<WorkerFAQPage />} />
            <Route path="/for-workers/:city" element={<CityWorkerLandingPage />} />
            <Route path="/markets/:city" element={<MarketPage />} />
            <Route path="/hire/:role/:city" element={<HireRoleCityPage />} />
            <Route path="/hire/:role" element={<HireRoleCityPage />} />
            <Route path="/gigs/:city/:role" element={<RoleCityGigsPage />} />
            <Route path="/gigs/:city" element={<CityGigsPage />} />
            <Route path="/roles/:role" element={<RolePage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/claim" element={<ClaimListingPage />} />
            <Route path="/claim/vendor-listing" element={<ClaimListingPage />} />
            <Route
              path="/workers/:id"
              element={isAuthenticated
                ? (user?.role === 'employer' || user?.role === 'admin'
                  ? <PublicWorkerProfilePage />
                  : <Navigate to={getDefaultAuthenticatedPath(user?.role)} replace />)
                : <SignedOutEmployerRouteRedirect />}
            />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/vendors/city/:city/:specialty" element={<VendorSpecialtyCityPage />} />
            <Route path="/vendors/city/:city" element={<CityVendorsPage />} />
            <Route path="/vendors/:slug" element={<VendorDetailPage />} />

            {/* Protected Routes — Worker. Discovery stays open to partial profiles; sensitive actions remain gated. */}
            <Route path="/dashboard" element={isAuthenticated ? (user?.role === 'employer' ? <Navigate to="/employer" /> : <RequireProfile><DashboardPage /></RequireProfile>) : <Navigate to="/login" />} />
            <Route path="/profile/setup" element={isAuthenticated ? <WorkerProfileSetupPage /> : <Navigate to="/login" />} />
            <Route path="/matches" element={isAuthenticated ? <MatchesPage /> : <SignedOutWorkerRouteRedirect />} />
            <Route path="/subscription" element={isAuthenticated ? (user?.role === 'worker' ? <RequireProfile><SubscriptionPage /></RequireProfile> : <Navigate to={getDefaultAuthenticatedPath(user?.role)} replace />) : <SignedOutWorkerRouteRedirect />} />
            <Route path="/insights" element={isAuthenticated ? <RequireProfile><WorkerInsightsPage /></RequireProfile> : <Navigate to="/login" />} />
            <Route path="/messages" element={isAuthenticated ? <RequireProfile><MessagesPage /></RequireProfile> : <Navigate to="/login" />} />
            <Route path="/applications" element={isAuthenticated ? <RequireProfile><ApplicationsPage /></RequireProfile> : <Navigate to="/login" />} />
            <Route path="/applications/:id" element={isAuthenticated ? <RequireProfile><ApplicationDetailPage /></RequireProfile> : <Navigate to="/login" />} />
            <Route path="/referrals" element={isAuthenticated ? <RequireProfile><ReferralPage /></RequireProfile> : <Navigate to="/login" />} />
            <Route path="/availability" element={isAuthenticated ? <RequireProfile><AvailabilityCalendarPage /></RequireProfile> : <Navigate to="/login" />} />
            <Route path="/notifications" element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/login" />} />
            <Route path="/settings" element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" />} />
            <Route path="/saved-jobs" element={isAuthenticated ? (user?.role === 'worker' ? <SavedJobsPage /> : <Navigate to={user?.role === 'employer' ? '/employer' : '/admin'} />) : <SignedOutWorkerRouteRedirect />} />
            <Route path="/job-board" element={<Navigate to="/jobs" replace />} />
            <Route path="/payments" element={isAuthenticated ? (user?.role === 'employer' ? <RequireEmployerProfile><PaymentsPage /></RequireEmployerProfile> : <RequireProfile><PaymentsPage /></RequireProfile>) : <Navigate to="/login" />} />

            {/* Protected Routes — Employer (RequireEmployerProfile redirects to /employer/setup if no company profile) */}
            <Route path="/employer/setup" element={isAuthenticated && user?.role === 'employer' ? <EmployerSetupPage /> : <Navigate to="/login" />} />
            <Route
              path="/employer"
              element={isRestoringAuthenticatedSession
                ? (
                  <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true" />
                    <span className="sr-only">Restoring your employer session.</span>
                  </div>
                )
                : isAuthenticated && user?.role === 'employer'
                  ? <RequireEmployerProfile><EmployerDashboardPage /></RequireEmployerProfile>
                  : <Navigate to="/login" />}
            />
            <Route path="/employer/subscription" element={isAuthenticated ? (user?.role === 'employer' ? <RequireEmployerProfile><EmployerSubscriptionPage /></RequireEmployerProfile> : <Navigate to={getDefaultAuthenticatedPath(user?.role)} replace />) : <SignedOutEmployerRouteRedirect />} />
            <Route path="/post-job" element={<Navigate to="/post-gig" replace />} />
            <Route path="/post-gig" element={isAuthenticated ? (user?.role === 'employer' ? <RequireEmployerProfile><PostGigPage /></RequireEmployerProfile> : <Navigate to={getDefaultAuthenticatedPath(user?.role)} replace />) : <SignedOutEmployerRouteRedirect />} />

            {/* Admin Routes */}
            <Route path="/admin" element={isAuthenticated && user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/login" />} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
